import React from 'react';
import { Button } from '@/components/shared/button';
import { Share2 } from 'lucide-react';
import { Weights, OpportunityScore } from '@/types/opportunity';
import { useShareAssessment } from '@/hooks/useShareAssessment';
import { toast } from 'sonner';
import { useAssessment } from '@/contexts/AssessmentContext';

interface ShareAssessmentButtonProps {
  weights: Weights;
  scoredOpportunities: OpportunityScore[];
}

const ShareAssessmentButton = ({ weights, scoredOpportunities }: ShareAssessmentButtonProps) => {
  const { scoringParams } = useAssessment();
  
  // Create extended weights object that includes scoringParams (same pattern as SaveAssessmentButton)
  const extendedWeights = {
    ...weights,
    _scoringParams: scoringParams
  };
  
  const { handleShare, isSharing } = useShareAssessment(extendedWeights, scoredOpportunities);

  const onShare = async () => {
    try {
      console.log('Share button clicked - starting share process...');
      const shareUrl = await handleShare();
      console.log('Generated share URL:', shareUrl);
      
      // The handleShare function already copies to clipboard, so we just need to show our success toast
      console.log('Showing success toast...');
      toast.success('Assessment Shared Successfully!', {
        description: 'Link copied to clipboard! You can now paste it anywhere to share.',
        duration: 8000,
      });
      console.log('Success toast should be displayed');
    } catch (error) {
      console.error('Error sharing assessment:', error);
      
      // Don't show error toast for authentication errors since useShareAssessment already shows one
      if (error instanceof Error && error.message === 'Authentication required') {
        console.log('Authentication error - letting useShareAssessment handle it');
        return; // Let the useShareAssessment hook handle the authentication flow
      }
      
      console.log('Showing error toast...');
      toast.error('Failed to generate share link. Please try again.');
    }
  };

  return (
    <Button
      variant="outline"
      onClick={onShare}
      className="gap-2"
      disabled={isSharing}
    >
      <Share2 className="w-4 h-4" />
      {isSharing ? 'Sharing...' : 'Share'}
    </Button>
  );
};

export default ShareAssessmentButton;